import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import QRCodeGenerator from "../QRCodeGenerator";
import Header from "../Header";
import Button from "../Button";
import PoweredBy from "../PoweredBy";

interface VoiceAssistantViewProps {}

const VoiceAssistantView: React.FC<VoiceAssistantViewProps> = () => {
  const navigate = useNavigate();
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedDevice, setConnectedDevice] = useState<string | null>(null);

  // Simulate checking for connected devices (replace with real WebSocket later)
  useEffect(() => {
    const checkConnection = setInterval(() => {
      // In production, check WebSocket for session join events
      // For demo, we'll just show the QR
    }, 2000);

    return () => clearInterval(checkConnection);
  }, [sessionId]);

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-brand-background to-brand-surface flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Header />

      <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
        {!isConnected ? (
          <>
            {/* Title */}
            <motion.div
              className="text-center mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="font-brand-primary font-medium text-[40px] text-brand-text-primary mb-4">
                Your Personal Assistant
              </h1>
              <p className="font-brand-primary font-light text-[20px] text-brand-text-secondary max-w-[500px]">
                Connect your phone to get personalized help as you browse the store
              </p>
            </motion.div>

            {/* QR Code */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <QRCodeGenerator
                mode="voice-assistant"
                sessionId={sessionId}
                storeId="lululemon-store-001"
                kioskId="pin-001"
                size={280}
              />
            </motion.div>

            {/* Features */}
            <motion.div
              className="mt-12 grid grid-cols-3 gap-8 max-w-[700px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <FeatureCard
                icon={
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                  </svg>
                }
                title="Voice Enabled"
                description="Ask questions naturally, just like talking to an associate"
              />
              <FeatureCard
                icon={
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                }
                title="Product Knowledge"
                description="Get details on fabrics, sizing, and care instructions"
              />
              <FeatureCard
                icon={
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
                  </svg>
                }
                title="Trade-In Info"
                description="Learn about our resale program and get value estimates"
              />
            </motion.div>
          </>
        ) : (
          <>
            {/* Connected State */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>
              <h2 className="font-brand-primary font-medium text-[36px] text-brand-text-primary mb-4">
                Device Connected
              </h2>
              <p className="font-brand-primary font-light text-[20px] text-brand-text-secondary mb-8">
                {connectedDevice || "Your phone"} is now connected. Start speaking to get help!
              </p>
              <Button variant="secondary" onClick={() => setIsConnected(false)}>
                Disconnect
              </Button>
            </motion.div>
          </>
        )}
      </div>

      {/* Bottom actions */}
      <div className="px-8 py-6 flex justify-between items-center border-t border-brand-border">
        <Button variant="tertiary" onClick={() => navigate("/")}>
          Back to Home
        </Button>
        <Button variant="primary" onClick={() => navigate("/before-start")}>
          Use Kiosk Instead
        </Button>
      </div>

      <PoweredBy />
    </motion.div>
  );
};

// Feature Card Component
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => (
  <div className="text-center">
    <div className="w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-brand-primary">
      {icon}
    </div>
    <h3 className="font-brand-primary font-medium text-[18px] text-brand-text-primary mb-2">
      {title}
    </h3>
    <p className="font-brand-primary font-light text-[14px] text-brand-text-muted">
      {description}
    </p>
  </div>
);

export default VoiceAssistantView;

